import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";

// Single-tier model (Task #136 cleanup): the only paid plan is "Pro" at
// $69/year. Tier ladders, monthly AI caps, and Starter/Premium variants
// have been removed — access is governed by `hasAccess` (trial OR active
// sub) and the legacy `tierAtLeast` helper has been deleted.
export type Tier = "free" | "pro";

export interface TierData {
  tier: Tier;
  label: string;
  aiUsed: number;
  isTrial: boolean;
  trialDaysLeft: number;
  hasActiveSubscription: boolean;
  /**
   * True when the user can make changes (admin, in trial, or active
   * subscription). When false the app falls into read-only mode: reads and
   * exports stay available but writes return 403 with `subscriptionExpired`.
   */
  hasAccess: boolean;
  /**
   * True when Stripe notified us of a failed renewal charge for this
   * teacher's subscription and we have not yet seen a subsequent
   * successful charge. The app uses this to show a "card declined"
   * banner so the teacher can update their payment method before they
   * lose write access.
   */
  paymentFailed: boolean;
  /**
   * True when the server is operating in closed-beta mode
   * (CLOSED_BETA_ENABLED=true). In this mode only admin users and users
   * with an unexpired betaAccessUntil get write access; the normal 14-day
   * trial path is disabled. Set to false (or omit the env var) for public
   * launch so the standard trial + Stripe logic takes over.
   */
  isClosedBeta: boolean;
  /**
   * True when the user's betaAccessUntil is set and in the future. Paired
   * with isClosedBeta to drive the "Beta access pending" UI gate.
   */
  betaAccessActive: boolean;
}

async function fetchTier(): Promise<TierData> {
  return customFetch<TierData>("/api/subscription/tier", { credentials: "include" });
}

export function useSubscriptionTier() {
  return useQuery<TierData>({
    queryKey: ["subscription", "tier"],
    queryFn: fetchTier,
    staleTime: 2 * 60_000,
    retry: false,
  });
}

/**
 * Convenience hook used by UI to switch into read-only mode after the trial
 * ends without a subscription. Returns:
 *  - `readOnly`: true when the user has lost write access. UI should disable
 *    Add/Edit/Delete buttons and show a banner explaining what happened.
 *  - `loading`: true while the tier query is in flight; treat as not-readonly
 *    so we never show the banner on first paint to active users.
 *  - `isTrial`, `trialDaysLeft`: passthrough so the trial banner can decide
 *    its own copy.
 */
export function useReadOnlyMode(): {
  readOnly: boolean;
  loading: boolean;
  isTrial: boolean;
  trialDaysLeft: number;
} {
  const { data, isLoading } = useSubscriptionTier();
  return {
    readOnly: !isLoading && !!data && data.hasAccess === false,
    loading: isLoading,
    isTrial: data?.isTrial ?? false,
    trialDaysLeft: data?.trialDaysLeft ?? 0,
  };
}
